// Live connection heartbeat (V0.4.15, spec Live Block R1.2): every 5 s while the tab is visible, a check passes when the
// site's presence channel is joined and a cheap database round trip (c3_my_access) answers within 5 s.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { supabase } from './supabase';
import { usePresence } from './presence';
import { BEAT_MS, deriveStatus, passes, type LiveStatus } from './connectionState';

type Conn = { status: LiveStatus; beat: number; lastBeatAt: Date | null; online: number; retry: () => void };
const ConnCtx = createContext<Conn>({ status: 'live', beat: 0, lastBeatAt: null, online: 1, retry: () => {} });
export const useConnection = () => useContext(ConnCtx);

async function ping(): Promise<{ ok: boolean; ms: number; auth: boolean }> {
  const t0 = Date.now();
  const timeout = new Promise<{ error: { message: string; code?: string } }>((r) => setTimeout(() => r({ error: { message: 'timeout' } }), BEAT_MS));
  const res = (await Promise.race([supabase.rpc('c3_my_access'), timeout])) as { error: { message: string; code?: string } | null };
  const ms = Date.now() - t0;
  const err = res.error;
  const auth = Boolean(err && /jwt|401|auth/i.test(`${err.message} ${err.code ?? ''}`));
  return { ok: !err, ms, auth };
}

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const { joined, others } = usePresence();
  const joinedRef = useRef(joined);
  joinedRef.current = joined;
  const started = useRef(Date.now());
  const lastPass = useRef<number | null>(null);
  const missed = useRef(false);
  const inFlight = useRef(false);
  const [beat, setBeat] = useState(0);
  const [now, setNow] = useState(Date.now());

  const check = useCallback(async () => {
    if (inFlight.current) return; // one check at a time
    inFlight.current = true;
    try {
      const r = await ping();
      if (r.auth) return; // let the session refresh run — never show offline for an expired token
      if (passes(joinedRef.current, r.ok, r.ms)) {
        lastPass.current = Date.now();
        missed.current = false;
        setBeat((b) => b + 1);
      } else missed.current = true;
    } catch {
      missed.current = true;
    } finally {
      inFlight.current = false;
      setNow(Date.now());
    }
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      void check();
      timer = setInterval(() => void check(), BEAT_MS);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const vis = () => (document.visibilityState === 'visible' ? start() : stop());
    const wake = () => {
      setNow(Date.now());
      if (document.visibilityState === 'visible') void check();
    };
    vis();
    const tick = setInterval(() => setNow(Date.now()), 1000); // status ages even when a check hangs
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('online', wake);
    window.addEventListener('offline', wake);
    window.addEventListener('focus', wake);
    return () => {
      stop();
      clearInterval(tick);
      document.removeEventListener('visibilitychange', vis);
      window.removeEventListener('online', wake);
      window.removeEventListener('offline', wake);
      window.removeEventListener('focus', wake);
    };
  }, [check]);

  // a newly joined channel → check straight away rather than waiting up to 5 s
  useEffect(() => {
    if (joined) void check();
  }, [joined, check]);

  const status = deriveStatus({ now, startedAt: started.current, lastPassAt: lastPass.current, missed: missed.current, online: navigator.onLine !== false });
  const value = useMemo<Conn>(
    () => ({ status, beat, lastBeatAt: lastPass.current ? new Date(lastPass.current) : null, online: others.length + 1, retry: () => void check() }),
    [status, beat, others.length, check],
  );
  return <ConnCtx.Provider value={value}>{children}</ConnCtx.Provider>;
}

/** Shown beside Save / Send buttons: saving pauses while offline (spec §4). */
export function OfflineNote() {
  const { status } = useConnection();
  return status === 'offline' ? <span className="conn-warn" role="status">Offline · changes paused — try again when Live</span> : null;
}
